import { Box, Spinner, Text } from "@primer/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { appConst } from "../../AppConst";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAuth } from "../../hooks/useAuth";
import { rootActions } from "../../state/actions/rootActions";

/**
 * Tách URL http(s) trong thông báo lỗi → render thành liên kết bấm được (mở tab mới).
 * Dùng cho thông báo "không có đợt đăng ký phù hợp" — backend nhúng URL cổng SV (config
 * StudentPortal:Url) vào message. Phần text còn lại giữ nguyên.
 */
const renderErrorWithLinks = (msg: string): React.ReactNode => {
    const parts = msg.split(/(https?:\/\/[^\s]+)/g);
    return parts.map((part, i) =>
        /^https?:\/\//.test(part) ? (
            <a
                key={i}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#0b5cc4", fontWeight: 600, textDecoration: "underline" }}
            >
                {part}
            </a>
        ) : (
            part
        )
    );
};

/** Logo Google chính thức (4 màu) — SVG inline, không phụ thuộc FontAwesome (FA bản này thiếu brand icon). */
const GoogleIcon = ({ size = 18 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true" focusable="false">
        <path fill="#4285F4" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
        <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
);

/** Logo Microsoft chính thức (4 ô vuông) — SVG inline. */
const MicrosoftIcon = ({ size = 18 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 23 23" aria-hidden="true" focusable="false">
        <path fill="#f25022" d="M1 1h10v10H1z" />
        <path fill="#7fba00" d="M12 1h10v10H12z" />
        <path fill="#00a4ef" d="M1 12h10v10H1z" />
        <path fill="#ffb900" d="M12 12h10v10H12z" />
    </svg>
);

/** Nút đăng nhập mạng xã hội dùng chung — logo + hover/active mượt, full-width. */
const OAuthButton = ({
    icon,
    label,
    onClick,
    disabled,
    large,
}: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    large?: boolean;
}) => (
    <Box
        as="button"
        type="button"
        onClick={onClick}
        disabled={disabled}
        sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: large ? 3 : 2,
            width: "100%",
            height: large ? "52px" : "42px",
            px: 3,
            fontSize: large ? 15 : 14,
            fontWeight: large ? 700 : 600,
            color: "#3c4043",
            bg: "#fff",
            border: "1px solid #dadce0",
            borderRadius: large ? 12 : 8,
            cursor: "pointer",
            boxShadow: large ? "0 1px 3px rgba(60,64,67,0.12)" : "none",
            transition: "box-shadow .2s ease, background-color .2s ease, border-color .2s ease, transform .1s ease",
            fontFamily:
                'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            "&:hover": {
                bg: "#f7f8fa",
                borderColor: "#c6c9ce",
                boxShadow: large ? "0 4px 14px rgba(60,64,67,0.22)" : "0 1px 4px rgba(60,64,67,0.2)",
            },
            "&:active": { bg: "#eceef1", transform: "translateY(1px)" },
            "&:disabled": { opacity: 0.55, cursor: "not-allowed", boxShadow: "none" },
        }}
    >
        <Box sx={{ display: "inline-flex", alignItems: "center" }}>{icon}</Box>
        {label}
    </Box>
);

/**
 * Clone Old `Account/Login.cshtml` — split card 900px:
 *   [Left: branding webName + tagline] | [Right: form đăng nhập]
 *
 * Form fields exact giống Old: username (mã SV) + password (show/hide eye toggle) + alert warning.
 * CAPTCHA reserved nhưng skip render vì backend mới chưa support.
 */
const LoginPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, errorMessage } = useAuth();
    const { webName, logoUrl } = appConst.branding;

    const [searchParams] = useSearchParams();
    // URL đặc biệt /login?masv=1 → buộc hiện lại form đăng nhập bằng mã SV + mật khẩu, kể cả khi
    // REACT_APP_OAUTH_ONLY=true (deployment chỉ-OAuth ẩn form). Dùng cho SV chưa liên kết OAuth /
    // hỗ trợ nội bộ. KHÔNG phải backdoor: form vẫn bắt buộc mật khẩu, đi qua đúng POST /api/auth/login.
    const forceMaSvLogin = searchParams.get("masv") === "031298";

    const { google: googleOn, microsoft: msOn, onlyOAuth } = appConst.oauth;
    const oauthEnabled = googleOn || msOn;
    // Chỉ ẩn đăng nhập mã SV khi đã bật ít nhất 1 provider OAuth — tránh khoá sạch đường đăng nhập.
    // forceMaSvLogin (?masv=1) override → vẫn hiện form mã SV (nút OAuth vẫn render bên dưới).
    const oauthOnly = oauthEnabled && onlyOAuth && !forceMaSvLogin;

    // SSO (VNPT / Nam Việt) bật theo REACT_APP_OIDC_UNIT. Khi bật → ẩn form mã SV/mật khẩu, chỉ
    // còn nút "Đăng nhập qua cổng tập trung" (+ Google/MS nếu được bật riêng). /login?masv=031298
    // override → hiện lại form mã SV cho admin / SV chưa liên kết SSO (vẫn đi đúng POST /api/auth/login).
    const ssoOn = appConst.sso.unit === "vnpt" || appConst.sso.unit === "namviet";
    const ssoShown = ssoOn && !forceMaSvLogin;
    // Form mã SV/mật khẩu bị ẩn khi: bật SSO HOẶC oauthOnly. masv override luôn hiện lại form.
    const hidePasswordForm = !forceMaSvLogin && (ssoOn || (oauthEnabled && onlyOAuth));

    const [maSv, setMaSv] = useState("");
    const [matKhau, setMatKhau] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    // Lỗi OAuth do backend 302 trả về qua ?oauth_error= (vd email chưa liên kết, cổng chưa mở).
    const oauthError = searchParams.get("oauth_error") || "";
    const displayError = errorMessage || oauthError;

    useEffect(() => {
        if (isAuthenticated) navigate("/", { replace: true });
    }, [isAuthenticated, navigate]);

    // Bắt đầu luồng OAuth — điều hướng cả trang sang endpoint backend /start (backend giữ ClientSecret).
    const startOAuth = (provider: "google" | "microsoft") => {
        window.location.href = `${appConst.baseApiURL}/auth/oauth/${provider}/start`;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!maSv.trim() || !matKhau) return;
        dispatch(rootActions.auth.LOGIN_START({ ma_sv: maSv.trim(), mat_khau: matKhau }));
    };

    return (
        <Box
            id="Login"
            sx={{
                background: "linear-gradient(135deg, #e8eefc 0%, #eef0f7 45%, #e7ecf7 100%)",
                width: "100%",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
            }}
        >
            <style>{`
                @keyframes loginCardIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
                /* Ô input login — scoped trong #formLogin để không ảnh hưởng trang khác */
                #formLogin .old-form-control,
                #formLogin .old-show-hide-password{
                    border:1px solid #d6dbe7;
                    border-radius:10px;
                    transition:border-color .15s ease, box-shadow .15s ease;
                }
                #formLogin .old-form-control{padding:10px 14px;color:#24292f;}
                #formLogin .old-form-control:focus,
                #formLogin .old-show-hide-password:focus-within{
                    border-color:#4763c4;
                    box-shadow:0 0 0 3px rgba(71,99,196,0.16);
                }
                #formLogin .old-form-control:disabled{background:#f5f6f8;}
                #formLogin .old-show-hide-password>input{padding:10px 14px;}
                #formLogin .old-show-hide-password .input-group-addon{background:#fff;color:#6a737d;}
            `}</style>
            <Box
                id="formLogin"
                sx={{
                    bg: "#fff",
                    width: "100%",
                    maxWidth: ["95%", "95%", 900],
                    p: ["20px 15px", "30px"],
                    border: "1px solid rgba(255,255,255,0.7)",
                    boxShadow: "0 18px 50px rgba(31, 45, 90, 0.16)",
                    display: "flex",
                    flexDirection: ["column", "column", "row"],
                    justifyContent: "space-between",
                    alignItems: ["center", "center", "stretch"],
                    gap: [3, 3, 4],
                    borderRadius: "20px",
                    animation: "loginCardIn .45s cubic-bezier(.2,.7,.3,1)",
                    fontFamily:
                        'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif',
                }}
                onKeyUp={(e) => {
                    if (e.key === "Enter") handleSubmit(e as any);
                }}
            >
                {/* Left — branding */}
                <Box
                    className="left"
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        p: [3, 4],
                        background: "linear-gradient(160deg, #eef3ff 0%, #f6f8fd 100%)",
                        borderRadius: 16,
                    }}
                >
                    <Box className="nameWebsite">
                        {logoUrl && (
                            <Box
                                sx={{
                                    width: ["96px", "128px"],
                                    height: ["96px", "128px"],
                                    mx: "auto",
                                    mb: 3,
                                    p: 2,
                                    bg: "#fff",
                                    borderRadius: "50%",
                                    boxShadow: "0 8px 22px rgba(31,45,90,0.12)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Box
                                    as="img"
                                    src={logoUrl}
                                    alt={webName}
                                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                        e.currentTarget.style.display = "none";
                                    }}
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                    }}
                                />
                            </Box>
                        )}
                        <Text
                            sx={{
                                fontSize: [16, 18],
                                display: "block",
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                color: "#1f2d5a",
                                lineHeight: 1.35,
                                mb: 2,
                            }}
                        >
                            {webName}
                        </Text>
                        <Text
                            sx={{
                                fontSize: 13,
                                display: "block",
                                fontWeight: 500,
                                textTransform: "uppercase",
                                letterSpacing: "0.6px",
                                color: "#6a73a0",
                            }}
                        >
                            Cổng thông tin đăng ký tín chỉ
                        </Text>
                    </Box>
                </Box>

                {/* Right — form */}
                <Box
                    className="right"
                    sx={{
                        width: ["100%", "100%", 360],
                        ml: [0, 0, 4],
                        pl: [0, 0, 2],
                        maxWidth: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                    }}
                >
                    <Text
                        className="title"
                        sx={{
                            display: "block",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            fontSize: 18,
                            letterSpacing: "0.5px",
                            textAlign: "center",
                            mb: 4,
                            color: "#1f2d5a",
                        }}
                    >
                        Đăng nhập tài khoản
                    </Text>

                    <Box as="form" onSubmit={handleSubmit} className="main">
                        {/* SSO cổng tập trung (VNPT / Nam Việt) — nút chính khi bật REACT_APP_OIDC_UNIT */}
                        {ssoShown && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                                <Text
                                    sx={{
                                        textAlign: "center",
                                        color: "#6a737d",
                                        fontSize: 13,
                                        mb: 1,
                                    }}
                                >
                                    Đăng nhập bằng tài khoản được nhà trường cấp
                                </Text>
                                <button
                                    type="button"
                                    className="old-btn old-btn-primary"
                                    style={{ width: "100%", fontWeight: "bold", padding: "10px" }}
                                    onClick={() => navigate("/signin-oidc")}
                                >
                                    Đăng nhập qua cổng tập trung
                                </button>
                            </Box>
                        )}

                        {!hidePasswordForm && (
                        <>
                        {/* Mã sinh viên */}
                        <Box className="rowForm" sx={{ my: 2 }}>
                            <Text
                                as="p"
                                sx={{
                                    mb: "5px",
                                    fontWeight: "bold",
                                    fontSize: 14,
                                    color: "#333",
                                }}
                            >
                                Mã sinh viên <Text sx={{ color: "red" }}>(*)</Text>
                            </Text>
                            <input
                                type="text"
                                className="old-form-control"
                                id="username"
                                value={maSv}
                                onChange={(e) => setMaSv(e.target.value)}
                                disabled={isLoading}
                                autoFocus
                            />
                        </Box>

                        {/* Mật khẩu với show/hide */}
                        <Box className="rowForm" sx={{ my: 2 }}>
                            <Text
                                as="p"
                                sx={{
                                    mb: "5px",
                                    fontWeight: "bold",
                                    fontSize: 14,
                                    color: "#333",
                                }}
                            >
                                Mật khẩu <Text sx={{ color: "red" }}>(*)</Text>
                            </Text>
                            <div className="old-show-hide-password">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    value={matKhau}
                                    onChange={(e) => setMatKhau(e.target.value)}
                                    disabled={isLoading}
                                />
                                <div className="input-group-addon">
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(v => !v)}
                                        aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                                        style={{
                                            background: "transparent",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: 0,
                                        }}
                                    >
                                        <i
                                            className={`fa ${
                                                showPassword ? "fa-eye" : "fa-eye-slash"
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            </div>
                        </Box>
                        </>
                        )}

                        {/* Error message */}
                        {displayError && (
                            <div
                                className="old-alert"
                                style={{
                                    background: "#f8d7da",
                                    borderColor: "#f5c6cb",
                                    color: "#721c24",
                                    marginTop: 8,
                                    marginBottom: 8,
                                }}
                            >
                                {renderErrorWithLinks(displayError)}
                            </div>
                        )}

                        {!hidePasswordForm && (
                        <>
                        {/* Warning note */}
                        <div className="old-alert old-alert-warning" style={{ padding: "4px 11px" }}>
                            Lưu ý: Sinh viên đổi mật khẩu trước khi đăng ký
                            <br />
                            Mật khẩu bao gồm chữ, số, ký tự đặc biệt.
                        </div>

                        {/* Submit */}
                        <Box
                            className="rowForm"
                            sx={{
                                my: 2,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <button
                                type="submit"
                                className="old-btn old-btn-primary"
                                style={{ width: "100%", fontWeight: "bold", padding: "8px 10px" }}
                                disabled={isLoading || !maSv.trim() || !matKhau}
                            >
                                {isLoading ? <Spinner size="small" /> : "Đăng nhập"}
                            </button>
                        </Box>
                        </>
                        )}

                        {/* Đăng nhập mạng xã hội */}
                        {oauthEnabled && (
                            <>
                                {!oauthOnly ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            my: 3,
                                            color: "#9aa0a6",
                                            fontSize: 12,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.4px",
                                        }}
                                    >
                                        <Box sx={{ flex: 1, height: "1px", bg: "#e6e6e6" }} />
                                        hoặc đăng nhập với
                                        <Box sx={{ flex: 1, height: "1px", bg: "#e6e6e6" }} />
                                    </Box>
                                ) : (
                                    <Text
                                        sx={{
                                            display: "block",
                                            textAlign: "center",
                                            color: "#6a737d",
                                            fontSize: 13,
                                            mt: 1,
                                            mb: 3,
                                        }}
                                    >
                                        Đăng nhập bằng tài khoản được nhà trường cấp
                                    </Text>
                                )}

                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 2,
                                        py: oauthOnly ? 2 : 0,
                                    }}
                                >
                                    {googleOn && (
                                        <OAuthButton
                                            large={oauthOnly}
                                            icon={<GoogleIcon size={oauthOnly ? 22 : 18} />}
                                            label="Google"
                                            onClick={() => startOAuth("google")}
                                            disabled={isLoading}
                                        />
                                    )}
                                    {msOn && (
                                        <OAuthButton
                                            large={oauthOnly}
                                            icon={<MicrosoftIcon size={oauthOnly ? 22 : 18} />}
                                            label="Microsoft"
                                            onClick={() => startOAuth("microsoft")}
                                            disabled={isLoading}
                                        />
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default LoginPage;
